import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef, type ReactNode } from "react";

import type { ScorecardViewState } from "./types";

import { cn } from "@/lib/utils";

/**
 * Identity + stack depth for the current scorecard screen. Depth changes
 * determine transition direction: deeper = push (enter from right),
 * shallower = pop (enter from left), equal = crossfade.
 */
export interface ScorecardScreen {
  key: string;
  depth: number;
}

export const getScorecardScreen = (
  view: ScorecardViewState,
  options: { isLoadingEmpty: boolean; hasScorecard: boolean },
): ScorecardScreen => {
  if (options.isLoadingEmpty) {
    return { key: "loading", depth: 0 };
  }
  if (!options.hasScorecard) {
    return { key: "empty", depth: 0 };
  }
  switch (view.view) {
    case "all-rows":
      return { key: "all-rows", depth: 1 };
    case "criterion":
      return { key: `criterion:${view.stepId}`, depth: 1 };
    case "criterion-edit":
      return { key: `criterion-edit:${view.stepId}`, depth: 2 };
    case "add-criterion":
      return { key: `add-criterion:${view.scope ?? "all"}`, depth: 1 };
    case "criterion-new":
      return { key: `criterion-new:${view.primitiveType}`, depth: 2 };
    case "row":
      return { key: `row:${view.rowIndex}`, depth: 2 };
    case "overview":
    default:
      return { key: "overview", depth: 0 };
  }
};

const SLIDE_DISTANCE = 24;
// Apple-style easing: quick start, long gentle settle.
const STACK_EASE = [0.32, 0.72, 0, 1] as const;

const variants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction * SLIDE_DISTANCE,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -SLIDE_DISTANCE,
  }),
};

interface ScorecardViewTransitionProps {
  screen: ScorecardScreen;
  className?: string;
  children: ReactNode;
}

/**
 * Stack-navigation transition for scorecard screens. Pushes slide the new
 * screen in from the right, pops from the left; sibling screens crossfade.
 */
const ScorecardViewTransition = ({ screen, className, children }: ScorecardViewTransitionProps) => {
  const prefersReducedMotion = useReducedMotion();
  const previousRef = useRef(screen);
  const directionRef = useRef(0);

  if (previousRef.current.key !== screen.key) {
    directionRef.current = Math.sign(screen.depth - previousRef.current.depth);
    previousRef.current = screen;
  }

  const direction = prefersReducedMotion ? 0 : directionRef.current;

  return (
    <div className="relative -mx-1 overflow-x-clip px-1">
      <AnimatePresence mode="popLayout" initial={false} custom={direction}>
        <motion.div
          key={screen.key}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: prefersReducedMotion ? 0.15 : 0.25, ease: STACK_EASE }}
          className={cn("w-full", className)}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ScorecardViewTransition;
